import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FileUpload from '../../../../admin/components/FileUpload';
import { Card, CardBody, CardHeader, CardTitle, Col, Row, Button, Tab, Tabs } from 'react-bootstrap';
import userRepository from '../../../services/repositories/user.repository';
import '../../../styles/products.scss';

const AddCustomer = ({ isEdit = false, customerId = null }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('client');
  const [clientData, setClientData] = useState({
    name: '',
    prenom: '',
    email: '',
    phone: '',
    address: '',
    role: 'client',
    salaire: '',
    image: null,
    imageUrl: null,
  });
  
  const [teamData, setTeamData] = useState({
    name: '',
    prenom: '',
    email: '',
    phone: '',
    address: '',
    role: 'manager',
    salaire: '',
    image: null,
    imageUrl: null,
  });
  
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customerLoaded, setCustomerLoaded] = useState(false);

  useEffect(() => {
    if (isEdit && customerId) {
      fetchCustomerData();
    } else {
      setCustomerLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, customerId]);

  const fetchCustomerData = async () => {
    try {
      setFetching(true);
      setError(null);
      
      const response = await userRepository.getById(customerId);
      const customer = response.data || response;
      
      if (!customer || !customer.id) {
        throw new Error('Customer data not found');
      }
      
      const { buildImageUrl } = await import('../../../utils/imageUrl');
      const imageUrl = buildImageUrl(customer.image);
      
      const customerData = {
        name: customer.name || '',
        prenom: customer.prenom || '',
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        role: customer.role || 'client',
        salaire: customer.salaire || '',
        image: customer.image || null, // Preserve existing image path
        imageUrl: imageUrl,
      };

      // Set data based on role
      if (customer.role === 'client') {
        setClientData(customerData);
        setActiveTab('client');
      } else {
        setTeamData(customerData);
        setActiveTab('equipe');
      }
      
      setCustomerLoaded(true);
    } catch (err) {
      console.error('Error fetching customer:', err);
      setError(err.message || 'Error loading customer data');
      setCustomerLoaded(true);
    } finally {
      setFetching(false);
    }
  };

  const handleImageChange = (files) => {
    if (files && files.length > 0) {
      const file = files[0];
      file.preview = URL.createObjectURL(file);
      if (activeTab === 'client') {
        setClientData(prev => ({ 
          ...prev, 
          image: file,
          imageUrl: file.preview 
        }));
      } else {
        setTeamData(prev => ({ 
          ...prev, 
          image: file,
          imageUrl: file.preview 
        }));
      }
    }
  };

  const handleRemoveImage = () => {
    if (activeTab === 'client') {
      setClientData(prev => ({ 
        ...prev, 
        image: null,
        imageUrl: null 
      }));
    } else {
      setTeamData(prev => ({ 
        ...prev, 
        image: null,
        imageUrl: null 
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (activeTab === 'client') {
      setClientData(prev => ({ ...prev, [name]: value }));
    } else {
      setTeamData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleTabChange = (key) => {
    // Copy current data to the other tab when switching
    if (key === 'client' && activeTab === 'equipe') {
      setClientData(teamData);
    } else if (key === 'equipe' && activeTab === 'client') {
      setTeamData(clientData);
    }
    setActiveTab(key);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const currentData = activeTab === 'client' ? clientData : teamData;
      const submitData = {
        name: currentData.name,
        prenom: currentData.prenom,
        email: currentData.email,
        phone: currentData.phone,
        address: currentData.address,
        role: currentData.role,
        salaire: currentData.salaire || null,
      };
      
      // Only include image if it's a new file (not existing path)
      if (currentData.image && currentData.image instanceof File) {
        submitData.image = currentData.image;
      }
      
      if (isEdit && customerId) {
        await userRepository.update(customerId, submitData);
      } else {
        await userRepository.create(submitData);
      }
      
      navigate('/admin/customer/customer-list');
    } catch (err) {
      console.error('Error saving user:', err);
      
      // Handle validation errors specifically
      if (err.response?.data?.errors) {
        const validationErrors = Object.values(err.response.data.errors).flat().join(', ');
        setError(`Erreurs de validation: ${validationErrors}`);
      } else {
        setError(err.response?.data?.message || err.message || 'Erreur lors de la sauvegarde');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/customer/customer-list');
  };

  if (fetching) {
    return (
      <Col xl={12} lg={12}>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Chargement des données du client...</p>
        </div>
      </Col>
    );
  }

  return (
    <Col xl={12} lg={12}>
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}
      
      <Card>
        <CardHeader>
          <CardTitle as={'h6'} className="mb-0" style={{ fontSize: '16px', fontWeight: '500' }}>
            {isEdit ? 'Modifier le Client / Équipe' : 'Nouveau Client / Équipe'}
          </CardTitle>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <Tabs
              activeKey={activeTab}
              onSelect={handleTabChange}
              className="mb-3"
            >
              <Tab eventKey="client" title="Client">
                {activeTab === 'client' && (
                  <>
                    <Row className="mt-4">
                  <Col lg={6}>
                    <div className="mb-3">
                      <label htmlFor="name" className="form-label">
                        Nom <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className="form-control"
                        placeholder="Nom"
                        value={clientData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="mb-3">
                      <label htmlFor="prenom" className="form-label">
                        Prénom <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        id="prenom"
                        name="prenom"
                        className="form-control"
                        placeholder="Prénom"
                        value={clientData.prenom}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col lg={6}>
                    <div className="mb-3">
                      <label htmlFor="email" className="form-label">
                        Email <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="form-control"
                        placeholder="Email"
                        value={clientData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="mb-3">
                      <label htmlFor="phone" className="form-label">
                        Téléphone
                      </label>
                      <input
                        type="text"
                        id="phone"
                        name="phone"
                        className="form-control"
                        placeholder="Téléphone"
                        value={clientData.phone}
                        onChange={handleInputChange}
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col lg={12}>
                    <div className="mb-3">
                      <label htmlFor="address" className="form-label">
                        Adresse
                      </label>
                      <input
                        type="text"
                        id="address"
                        name="address"
                        className="form-control"
                        placeholder="Adresse"
                        value={clientData.address}
                        onChange={handleInputChange}
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col lg={6}>
                    <div className="mb-3">
                      <label htmlFor="role" className="form-label">
                        Rôle
                      </label>
                      <input
                        type="text"
                        id="role"
                        name="role"
                        className="form-control"
                        value="client"
                        readOnly
                        disabled={activeTab !== 'client'}
                      />
                    </div>
                  </Col>
                </Row>
                <Row className="mb-4">
                  <Col lg={12}>
                    <div className="mb-3">
                      <label className="form-label">
                        Image
                      </label>
                      <div className={activeTab !== 'client' ? 'opacity-50' : ''}>
                        {clientData.imageUrl ? (
                          <div className="text-center">
                            <div className="position-relative d-inline-block">
                              <img
                               src={clientData.imageUrl}
                                alt="Image"
                                className="img-fluid rounded border"
                                style={{ maxWidth: '300px', maxHeight: '300px', objectFit: 'cover' }}
                              />
                              <Button
                                variant="danger"
                                size="sm"
                                className="position-absolute top-0 end-0 m-2"
                                onClick={handleRemoveImage}
                                style={{ zIndex: 10 }}
                                disabled={activeTab !== 'client'}
                              >
                                ×
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <FileUpload 
                            title="" 
                            onFilesChange={activeTab === 'client' ? handleImageChange : () => {}}
                            maxFiles={1}
                          />
                        )}
                      </div>
                      {activeTab !== 'client' && (
                        <small className="text-muted">Sélectionnez l'onglet Client pour modifier l'image</small>
                      )}
                    </div>
                  </Col>
                </Row>
                </>
                )}
              </Tab>
              <Tab eventKey="equipe" title="Équipe">
                {activeTab === 'equipe' && (
                  <>
                    <Row className="mt-4">
                  <Col lg={6}>
                    <div className="mb-3">
                      <label htmlFor="name" className="form-label">
                        Nom <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className="form-control"
                        placeholder="Nom"
                        value={teamData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="mb-3">
                      <label htmlFor="prenom" className="form-label">
                        Prénom <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        id="prenom"
                        name="prenom"
                        className="form-control"
                        placeholder="Prénom"
                        value={teamData.prenom}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col lg={6}>
                    <div className="mb-3">
                      <label htmlFor="email" className="form-label">
                        Email <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="form-control"
                        placeholder="Email"
                        value={teamData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="mb-3">
                      <label htmlFor="phone" className="form-label">
                        Téléphone
                      </label>
                      <input
                        type="text"
                        id="phone"
                        name="phone"
                        className="form-control"
                        placeholder="Téléphone"
                        value={teamData.phone}
                        onChange={handleInputChange}
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col lg={12}>
                    <div className="mb-3">
                      <label htmlFor="address" className="form-label">
                        Adresse
                      </label>
                      <input
                        type="text"
                        id="address"
                        name="address"
                        className="form-control"
                        placeholder="Adresse"
                        value={teamData.address}
                        onChange={handleInputChange}
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col lg={6}>
                    <div className="mb-3">
                      <label htmlFor="role" className="form-label">
                        Rôle <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-control"
                        id="role"
                        name="role"
                        value={teamData.role}
                        onChange={handleInputChange}
                        required
                        disabled={activeTab !== 'equipe'}
                      >
                        <option value="">Sélectionner un rôle</option>
                        <option value="manager">Manager</option>
                        <option value="distributeur">Distributeur</option>
                      </select>
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="mb-3">
                      <label htmlFor="salaire" className="form-label">
                        Salaire
                      </label>
                      <input
                        type="number"
                        id="salaire"
                        name="salaire"
                        className="form-control"
                        placeholder="Salaire"
                        value={teamData.salaire}
                        onChange={handleInputChange}
                        step="0.01"
                        disabled={activeTab !== 'equipe'}
                      />
                    </div>
                  </Col>
                </Row>
                <Row className="mb-4">
                  <Col lg={12}>
                    <div className="mb-3">
                      <label className="form-label">
                        Image
                      </label>
                      <div className={activeTab !== 'equipe' ? 'opacity-50' : ''}>
                        {teamData.imageUrl ? (
                          <div className="text-center">
                            <div className="position-relative d-inline-block">
                              <img
                              src={teamData.imageUrl}
                                alt="Image"
                                className="img-fluid rounded border"
                                style={{ maxWidth: '300px', maxHeight: '300px', objectFit: 'cover' }}
                              />
                              <Button
                                variant="danger"
                                size="sm"
                                className="position-absolute top-0 end-0 m-2"
                                onClick={handleRemoveImage}
                                style={{ zIndex: 10 }}
                                disabled={activeTab !== 'equipe'}
                              >
                                ×
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <FileUpload 
                            title="" 
                            onFilesChange={activeTab === 'equipe' ? handleImageChange : () => {}}
                            maxFiles={1}
                          />
                        )}
                      </div>
                      {activeTab !== 'equipe' && (
                        <small className="text-muted">Sélectionnez l'onglet Équipe pour modifier l'image</small>
                      )}
                    </div>
                  </Col>
                </Row>
                </>
                )}
              </Tab>
            </Tabs>
            
            <div className="p-3 bg-light mb-3 rounded">
              <Row className="justify-content-end g-2">
                <Col lg={2}>
                  <Button 
                    variant="dark" 
                    className="w-100" 
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Annuler
                  </Button>
                </Col>
                <Col lg={2}>
                  <Button 
                    variant="primary" 
                    className="w-100" 
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? 'Enregistrement...' : (isEdit ? 'Modifier' : 'Créer')}
                  </Button>
                </Col>
              </Row>
            </div>
          </form>
        </CardBody>
      </Card>
    </Col>
  );
};

export default AddCustomer;